import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
} from "lucide-react";
import Logo from "../assets/logo.png";
import StethoscopeIcon from "../assets/icons/stethoscope.png";
import DefenceIcon from "../assets/icons/defence.png";
import WalletIcon from "../assets/icons/purse.png";
import HospitalIcon from "../assets/icons/hospital.png";

const legalContent = {
  terms: `QUREO TERMS & CONDITIONS
Effective Date: March 24, 2026
Last Updated: March 24, 2026

1. Introduction
Welcome to Qureo.
Qureo Health (“Qureo”, “we”, “us”, or “our”) provides a digital health platform that enables users to access AI-powered health guidance, virtual consultations, pharmacy services, diagnostics, health wallets, microinsurance, and related services.
By accessing or using Qureo, you agree to these Terms & Conditions (“Terms”). If you do not agree, please do not use the platform.

2. Nature of Services
Qureo is a technology platform, not a healthcare provider.
We:
Facilitate access to licensed healthcare providers
Provide AI-powered health guidance tools
Enable access to pharmacy, lab, and financial services
We do not directly provide medical care, except through independent providers.

3. Eligibility
To use Qureo, you must:
Be at least 18 years old, or use the platform with parental/guardian consent
Provide accurate and complete information
Use the platform in accordance with applicable laws

4. Account Registration
You agree to:
Provide accurate personal information
Keep your login credentials secure
Notify us of unauthorized access
You are responsible for all activities under your account.

5. Use of the Platform
You agree to use Qureo only for lawful purposes.
You must not:
Provide false or misleading health information
Misuse the platform or attempt to disrupt services
Access other users’ data without authorization
Use the platform for fraud or illegal activities

6. AI-Powered Services (Symptom Checker)
Qureo includes AI-based tools for health guidance.
By using these tools, you agree:
AI provides guidance only, not medical diagnosis
Outputs may not be accurate or complete
You will not rely solely on AI for medical decisions
You will seek professional care when needed

7. Medical Disclaimer
Qureo does not replace professional medical advice.
Always consult a licensed healthcare provider
Seek immediate care in emergencies
Do not delay treatment based on app results

8. Healthcare Providers
Qureo connects you to independent providers.
Providers are responsible for their own services
Qureo does not control clinical decisions
You engage providers at your own discretion

9. Pharmacy and Lab Services
Prescriptions must be issued by licensed professionals
Pharmacies and labs are responsible for their services
Qureo facilitates access but does not manufacture or dispense products

10. Payments, Wallet, and Microinsurance
Qureo may provide financial services including a health wallet and insurance access.
You agree that:
All payments are processed through authorized partners
You are responsible for reviewing transactions
Insurance services are provided by licensed insurers
Qureo is not an insurer or financial institution.

11. Fees and Charges
Some services may require payment
Prices are displayed before confirmation
Fees may change with notice

12. Data Privacy
Your use of Qureo is governed by our Privacy Policy.
By using Qureo, you consent to the collection and use of your data as described in the Privacy Policy.

13. Intellectual Property
All content on Qureo, including:
Design, branding, and software
AI models and algorithms
Text, graphics, and features
…is owned by Qureo or its licensors.
You may not copy, modify, or distribute without permission.

14. Service Availability
We aim to provide reliable service, but:
Qureo may be unavailable due to maintenance or technical issues
Features may change or be discontinued
We do not guarantee uninterrupted access.

15. Limitation of Liability
To the maximum extent permitted by law:
Qureo is not liable for:
Medical decisions made by users or providers
Errors or inaccuracies in AI outputs
Delays or service interruptions
Loss of data or unauthorized access
Your use of Qureo is at your own risk.

16. Indemnification
You agree to indemnify Qureo against claims arising from:
Misuse of the platform
Violation of these Terms
Inaccurate information you provide

17. Suspension and Termination
We may suspend or terminate your access if:
You violate these Terms
Your activity poses risk to users or the platform
Required by law

18. Third-Party Services
Qureo integrates with third-party providers.
We are not responsible for:
Their services
Their policies
Their performance

19. Emergency Use Disclaimer
Qureo is not an emergency service.
Do not use Qureo for urgent or life-threatening conditions.
Seek immediate medical attention.

20. Changes to Terms
We may update these Terms.
Continued use of Qureo means you accept the updated Terms.

21. Governing Law
These Terms are governed by applicable laws in countries where Qureo operates, including the Republic of Liberia.

22. Contact Information
Qureo Health
Email: [Insert Email]
Phone: [Insert Phone Number]
Address: [Insert Address]

23. Acceptance
By using Qureo, you confirm that you:
Have read and understood these Terms
Agree to be bound by them


QUREO TERMS & CONDITIONS (APP VERSION)

Welcome to Qureo
Qureo is a digital health platform that helps you access:
AI-powered health guidance
Licensed doctors and consultations
Pharmacy and lab services
Health wallet and microinsurance

By using Qureo, you agree to the following terms.

1. Qureo is a Platform
Qureo connects you to healthcare services.
We are not a hospital or doctor
Medical care is provided by independent licensed providers
Providers are responsible for the care they deliver

2. AI Health Guidance
Qureo includes an AI symptom checker.
By using it, you agree:
It provides guidance only, not diagnosis
Results may not always be accurate
You will not rely only on AI for medical decisions
You can always consult a real healthcare provider.

3. Medical Disclaimer
Qureo does not replace professional medical advice
Always seek care from a licensed provider
Do not ignore symptoms or delay treatment
If you have an emergency, seek immediate care.

4. Your Responsibilities
You agree to:
Provide accurate and honest information
Use the app responsibly
Keep your account secure

5. Payments, Wallet & Insurance
Payments are processed through secure partners
Wallet and insurance services are provided by licensed partners
Qureo is not a bank or insurance provider

6. Privacy
Your data is handled according to our Privacy Policy.
We:
Protect your data with secure systems
Share only when necessary to provide services
Never sell your personal or health data

7. Availability
We aim to keep Qureo running smoothly, but:
Services may occasionally be unavailable
Features may change over time

8. Limitation of Liability
Qureo is not responsible for:
Medical decisions made using the app
Outcomes of care provided by third parties
Errors in AI-generated guidance
Use of the app is at your own discretion.

9. Updates
We may update these terms from time to time.
Continued use means you accept the updated version.

10. Contact
Qureo Health
[Insert Email]
[Insert Phone]


QUREO PROVIDER TERMS & CONDITIONS
Effective Date: March 24, 2026

1. Introduction
These Provider Terms govern the participation of healthcare providers, pharmacies, laboratories, and other service partners (“Provider”) on the Qureo platform.
By onboarding to Qureo, you agree to these Terms.

2. Scope of Services
Providers may use Qureo to:
Deliver consultations (chat, audio, video)
Fulfill prescriptions and medication orders
Provide diagnostic and lab services
Support referrals and follow-up care
Providers operate as independent entities, not employees of Qureo.

3. Licensing and Compliance
Providers must:
Hold valid licenses and certifications in their jurisdiction
Comply with all applicable healthcare laws and regulations
Maintain professional standards of care
Qureo may request verification documents at any time.

4. Clinical Responsibility
Providers are fully responsible for clinical decisions and care delivered
Qureo does not practice medicine and does not interfere with clinical judgment
Providers must ensure safe, evidence-based care

5. Use of AI Tools
Qureo may provide AI-assisted insights.
Providers agree that:
AI outputs are decision-support only
Final decisions must be made by the Provider
Providers must exercise independent clinical judgment

6. Patient Interaction and Conduct
Providers must:
Treat patients with professionalism, respect, and confidentiality
Provide clear and accurate medical information
Avoid discrimination, abuse, or unethical conduct
Qureo may suspend providers for misconduct.

7. Data Protection and Confidentiality
Providers agree to:
Protect patient data in accordance with applicable data protection laws
Use Qureo systems only for authorized purposes
Not store or share patient data outside approved systems

8. Service Quality and Performance
Qureo may monitor:
Response times
Patient feedback
Quality of care indicators
Providers are expected to meet minimum service standards.

9. Payments and Revenue Sharing
Providers will be compensated based on agreed pricing or revenue-sharing models
Payment terms will be defined in a separate commercial agreement or addendum
Providers are responsible for applicable taxes

10. Pharmacy and Lab Responsibilities
Pharmacies must:
Dispense only approved and authentic medications
Follow prescription requirements
Maintain proper storage and cold chain where applicable

Labs must:
Ensure accuracy and quality of diagnostic results
Use certified processes and equipment
Deliver results within agreed timelines

11. Liability
Providers are responsible for their services and outcomes
Qureo is not liable for clinical decisions or malpractice
Providers must maintain appropriate professional liability coverage (where applicable)

12. Platform Use Restrictions
Providers must not:
Use Qureo to solicit patients outside the platform
Misrepresent qualifications or services
Manipulate pricing or services unfairly

13. Suspension and Termination
Qureo may suspend or terminate access if:
Licensing is invalid or expired
There is misconduct or breach of these Terms
Required by law or regulatory authority

14. Governing Law
These Terms are governed by the laws of:
[Insert Country, e.g., Republic of Liberia]

15. Contact
Qureo Health
Email: [Insert Email]


Symptom Checker Legal Disclaimer

Short Banner
Qureo AI is not a doctor.
This tool provides guidance only, not a diagnosis. Always consult a licensed healthcare provider for medical advice.

Expanded Disclaimer
Qureo’s AI-powered symptom checker is designed to provide general health guidance based on the information you provide.
It does not provide medical diagnosis, treatment, or prescriptions
It is not a substitute for professional medical advice
Results may not be fully accurate or complete

When to Seek Care Immediately
If you experience any of the following, seek urgent medical attention:
Severe pain or difficulty breathing
Uncontrolled bleeding
Loss of consciousness
Seizures or chest pain
Any life-threatening condition

Your Responsibility
By using this feature, you agree that:
You will not rely solely on AI for medical decisions
You will seek professional care when needed
You provide accurate and truthful information

Human Care is Always Available
Qureo allows you to:
Connect with a licensed healthcare provider
Book lab tests
Order medications safely


QUREO AI CLINICAL SAFETY PROTOCOL
Version: 1.0
Applies to: Qureo AI Symptom Checker (SmartCheck)
Last Updated: March 24, 2026

1. Purpose
This protocol defines how Qureo’s AI system:
Safely assesses user-reported symptoms
Identifies clinical risk levels
Escalates high-risk cases
Ensures users receive appropriate care

2. Core Principle
AI must never replace clinical judgment. It must safely guide users toward appropriate care.

3. Risk Stratification Framework
Every interaction must end in one of four risk levels:

Level 1: Emergency (RED)
Immediate danger to life or severe harm
Examples:
Chest pain
Difficulty breathing
Unconsciousness
Severe bleeding
Seizures
Stroke symptoms

System Action:
Stop AI interaction immediately
Display emergency message
Prompt user to:
Call emergency services
Go to nearest facility
Disable further AI guidance

Level 2: Urgent (ORANGE)
Serious condition requiring same-day care
Examples:
High fever (especially in children)
Severe abdominal pain
Pregnancy complications
Persistent vomiting
Signs of infection

System Action:
Strong recommendation to see a doctor within 24 hours
Offer:
Immediate virtual consultation
Nearest facility guidance
Limit AI advice to supportive guidance only

Level 3: Moderate (YELLOW)
Non-urgent but requires medical attention
Examples:
Ongoing symptoms
Mild infections
Skin conditions
Digestive issues

System Action:
Recommend consultation (virtual or in-person)
Provide general health guidance
Offer booking options

Level 4: Low Risk (GREEN)
Mild or self-limiting conditions
Examples:
Common cold
Mild headache
Minor fatigue

System Action:
Provide self-care guidance
Offer escalation option if symptoms worsen

4. Red Flag Detection Engine
The AI must always check for non-negotiable red flags.
Immediate escalation triggers:
Chest pain or pressure
Difficulty breathing
Loss of consciousness
Severe bleeding
Sudden confusion
Severe headache with neurological signs
Seizures
Suicidal thoughts or self-harm indicators
Severe allergic reactions
Pregnancy + bleeding or severe pain

If detected:
Automatically classify as RED or ORANGE
Override all other logic

5. High-Risk Population Rules
AI must apply stricter thresholds for:
Children (Under 5)
Fever = urgent
Reduced feeding = urgent

Pregnant Women
Any bleeding = urgent
Severe pain = urgent

Elderly (60+)
Lower threshold for escalation
Multi-symptom cases = higher risk

Chronic Conditions (HIV, TB, Diabetes, Hypertension)
Symptoms escalate faster
Medication issues flagged as urgent

6. AI Output Safety Rules
AI must:
Avoid definitive diagnoses
Use language like:
“This may be…”
“You could be experiencing…”
Always include:
Uncertainty disclaimer
Clear next steps
Never:
Prescribe restricted medications
Recommend unsafe treatments

7. Mandatory Escalation Pathways
For ORANGE and RED cases:
System must offer:
Talk to a doctor now
Find nearest facility
Call emergency help

For Qureo integration:
Direct link to:
Virtual consult
Transport (Moovo integration potential)

8. Conversation Safety Guardrails
AI must:
Ask structured follow-up questions
Avoid long, confusing explanations
Prioritize clarity and action

Forbidden behaviors:
Giving absolute reassurance in uncertain cases
Ignoring worsening symptoms
Providing conflicting advice

9. User Responsibility Prompt
At key points, remind users:
This tool does not replace a doctor. Please seek care if symptoms worsen.

10. Continuous Monitoring & Improvement
Qureo must:
Log anonymized interactions
Track:
Escalation accuracy
Missed red flags
Conduct periodic clinical reviews
Update protocols regularly

11. Human Oversight Layer
Clinical advisors review system behavior
Edge cases are audited
Feedback loop into AI improvements

12. Incident Response (Clinical Risk)
If harm is reported:
Log the case
Review AI interaction
Identify failure point
Update system rules
Report (if required by regulation)

13. Localization for Qureo (Africa Context)
Protocol must account for:
Limited access to emergency services
Rural vs urban care differences
Cultural health-seeking behavior

Example:
Instead of only:
Call emergency services
Also include:
Go to the nearest clinic or health center immediately

14. UX Implementation
Must be embedded in UI:
Risk color coding (Red, Orange, Yellow, Green)
Clear next step buttons
Minimal text, high clarity


QUREO TELEMEDICINE POLICY
Version: 1.0
Effective Date: March 24, 2026
Applies to: All virtual care services delivered through Qureo

1. Purpose
This policy defines how telemedicine services are delivered on Qureo to ensure:
Safe, high-quality care
Compliance with applicable regulations
Protection of patient rights and data
Clear roles and responsibilities for providers

2. Scope
This policy applies to:
Doctors and licensed healthcare providers
Pharmacies and diagnostic partners
Qureo platform operations
All users accessing virtual care

Services include:
Chat, audio, and video consultations
Remote triage and follow-up
Prescription and referral services

3. Definition of Telemedicine
Telemedicine refers to the remote delivery of healthcare services using digital communication tools without requiring physical presence.

4. Guiding Principles
All telemedicine services on Qureo must:
Be patient-centered and safe
Maintain clinical quality equivalent to in-person care (where appropriate)
Respect privacy and confidentiality
Ensure informed consent
Promote equity and accessibility, especially in low-resource settings

5. Provider Eligibility and Licensing
Providers must:
Hold valid and current licenses in their country of practice
Be registered with relevant regulatory bodies
Provide services within their scope of practice

Cross-Border Care:
Only permitted where legally allowed
Must comply with local regulations in the patient’s country

6. Patient Identification and Consent
Before consultation:
Providers must:
Confirm patient identity (name, phone, or ID where applicable)
Obtain informed consent for telemedicine

Consent must include:
Understanding of limitations of virtual care
Agreement to proceed

7. Appropriate Use of Telemedicine
Suitable for:
Mild to moderate conditions
Follow-up care
Medication reviews
Health education and counseling
Sexual and reproductive health services

Not suitable for:
Emergencies
Severe or unstable conditions
Cases requiring physical examination

8. Clinical Safety and Escalation
Providers must:
Follow Qureo’s AI Clinical Safety Protocol
Identify red flags and high-risk symptoms
Escalate to:
In-person care
Emergency services where necessary

9. Prescribing Guidelines
Providers must:
Prescribe only when clinically appropriate
Follow national prescribing regulations
Avoid prescribing controlled substances unless legally permitted

Required:
Clear dosage and instructions
Documentation of prescription rationale

10. Documentation and Record Keeping
Providers must:
Maintain accurate consultation records
Document:
Symptoms
Assessment
Advice given
Prescriptions and referrals

Records must be securely stored within Qureo systems.

11. Data Privacy and Confidentiality
All providers must:
Protect patient data at all times
Use only Qureo-approved systems for communication
Avoid storing patient data outside the platform

12. Quality of Care Standards
Providers must:
Deliver evidence-based care
Communicate clearly and respectfully
Avoid misdiagnosis due to limitations of virtual care

Qureo may monitor:
Consultation quality
Response times
Patient feedback

13. Technology and Connectivity Considerations
Given the African context:
Providers should adapt to low-bandwidth environments
Use:
Chat-based care where needed
Audio over video when connectivity is limited

14. Pharmacy and Lab Integration
Providers must:
Prescribe through approved workflows
Ensure prescriptions are valid and legible

Pharmacies and labs must:
Fulfill orders based on valid prescriptions
Maintain quality and regulatory compliance

15. Payments and Transparency
Fees must be clear before consultation
Providers must not request payments outside Qureo
All transactions should occur through approved systems

16. Professional Conduct
Providers must:
Treat patients with dignity and respect
Maintain ethical standards
Avoid discrimination or abuse

17. Incident Reporting
Providers must report:
Adverse events
Misdiagnosis concerns
System or platform issues

Qureo will:
Investigate incidents
Take corrective action

18. Limitations of Telemedicine
Users and providers acknowledge:
Telemedicine has limitations compared to physical care
Some conditions cannot be fully assessed remotely

19. Liability
Providers are responsible for their clinical decisions
Qureo facilitates care but does not practice medicine

20. Monitoring and Compliance
Qureo may:
Audit consultations
Suspend providers for non-compliance
Update policies based on regulatory changes

21. Localization (Africa-Specific Considerations)
This policy recognizes:
Limited emergency infrastructure in some areas
Cultural and language differences
Variability in regulatory maturity
Qureo adapts care pathways accordingly.

22. Updates to Policy
Qureo may update this policy as needed.
Providers will be notified of significant changes.

23. Contact
Qureo Health
Email: [Insert Email]`,
  privacy: `QUREO PRIVACY POLICY
Effective Date: March 24, 2026
Last Updated: March 24, 2026

1. Introduction
Qureo Health (“Qureo”, “we”, “us”, or “our”) is committed to protecting the privacy, confidentiality, and security of personal data, especially sensitive health information.
Qureo is a digital health platform providing AI-powered health support, virtual consultations, ePharmacy, diagnostics, health wallets, microinsurance, and related services across mobile, web, and low-connectivity channels (USSD/SMS).
This Privacy Policy explains how we collect, use, disclose, process, and safeguard your personal data when you use Qureo.

2. Legal and Regulatory Alignment
Qureo aligns with:
Applicable national laws in countries of operation, including Liberia
Regional frameworks such as ECOWAS data protection principles
International standards including:
GDPR principles (lawfulness, fairness, transparency)
HIPAA-inspired safeguards for health data
WHO digital health data governance guidance

3. Scope of This Policy
This Policy applies to:
Mobile applications and web platforms
USSD, SMS, and low-bandwidth services
Interactions with healthcare providers via Qureo
All personal data processed by Qureo

4. Definitions
Personal Data: Information that identifies or can identify an individual
Sensitive Data: Includes health, biometric, and financial data
Processing: Collection, storage, use, sharing, or analysis of data
Data Controller: Qureo determines how data is used
Data Processor: Third parties processing data on our behalf

5. Legal Basis for Processing
We process your data based on:
Your consent
Provision of healthcare services
Legal and regulatory obligations
Legitimate interests such as system improvement and security
Public health needs where applicable

6. Information We Collect
6.1 Personal Information
Name, phone number, email
Date of birth, gender
Location

6.2 Health Information (Sensitive)
Symptoms and medical history
Prescriptions and medications
Lab results and diagnostics
Sexual and reproductive health data

6.3 Financial and Insurance Information
Health wallet balances and transactions
Payment records (e.g., mobile money)
Insurance enrollment, premiums, and claims

6.4 Technical and Usage Data
Device information and IP address
App usage and interaction data
Logs and analytics

6.5 Service Interaction Data
Appointments and consultations
Pharmacy orders
Lab requests
Emergency or transport requests

7. How We Use Your Information
We use your data to:
Deliver healthcare services and platform functionality
Provide AI-powered symptom checking and guidance
Facilitate consultations, pharmacy, and diagnostics
Enable payments, wallet, and insurance services
Improve user experience and system performance
Generate anonymized insights for public health
Detect fraud and ensure platform security

8. AI and Automated Decision-Making
Qureo uses artificial intelligence to support healthcare delivery.

Nature of AI
AI provides guidance and decision-support, not diagnosis
Outputs depend on user inputs and available data

Human Oversight
High-risk cases may be escalated to licensed providers
Users can request human care at any time

Explainability
We aim to provide understandable reasoning behind AI outputs.

Bias Mitigation
We actively:
Use diverse and representative datasets
Monitor outputs for disparities
Continuously improve model performance

9. Microinsurance and Health Wallet Data Handling
Qureo provides financial tools to improve healthcare access.

Use of Financial Data
We use financial data to:
Process payments and transactions
Manage wallet balances
Enable insurance enrollment and claims

Data Sharing
Financial data may be shared with:
Licensed insurers
Payment processors and mobile money providers
Healthcare partners for claims processing

Security
Encrypted transactions
Secure authentication
Fraud monitoring systems

10. Data Sharing and Disclosure
We may share your data with:

Healthcare Providers
Doctors, pharmacies, labs, and care teams

Financial and Insurance Partners
Mobile money providers and insurers

Technology Providers
Cloud infrastructure and analytics services

Government Authorities
Where required by law or for public health purposes

Data Processors
Third-party service providers acting on our behalf under strict agreements

Qureo does not sell personal or health data.

11. Cross-Border Data Transfers
Your data may be processed outside your country.
We ensure:
Appropriate safeguards
Contractual protections
Compliance with international standards

12. Data Security
We implement strong safeguards, including:
Encryption (in transit and at rest)
Role-based access controls
Multi-factor authentication
Continuous monitoring and audits

Additional Safeguards
Access logging and monitoring
Minimum necessary data access
Incident response protocols

13. Data Retention
We retain data only as long as necessary for:
Service delivery
Legal and regulatory compliance
Public health and system needs

Medical records are retained in accordance with national health regulations.

14. Your Rights
You may have the right to:
Access your data
Correct inaccurate information
Request deletion
Withdraw consent
Request data portability
Object to certain processing

Contact: [Insert Email]

15. Children’s Privacy
We do not knowingly collect data from minors without parental or guardian consent.

16. Device Permissions
With your consent, Qureo may access:
Camera and microphone
Storage and device features
Location services
Health data integrations (e.g., wearables)

You can manage permissions in your device settings.

17. Low-Connectivity and Offline Services
For USSD, SMS, or low-bandwidth access:
Limited data may be collected
Data is securely transmitted and stored
Synchronization occurs when connectivity is restored

18. Public Health and Data Governance
Qureo may use anonymized and aggregated data to:
Support Ministry of Health planning
Strengthen health systems
Inform policy and decision-making

No personally identifiable data is shared without consent unless required by law.

19. Third-Party Services
Qureo integrates with external providers.
We are not responsible for their privacy practices. Users should review their policies.

20. Data Breach Notification
In the event of a data breach, Qureo will:
Investigate and contain the incident
Notify affected users and authorities where required
Take corrective actions

21. Risks of Digital Platforms
While we implement strong safeguards, users acknowledge:
Digital systems carry inherent risks
Unauthorized access may occur despite safeguards

22. Updates to This Policy
We may update this Policy periodically.
Material changes will be communicated through the platform.

23. Contact Information
Qureo Health
Email: [Insert Email]
Phone: [Insert Phone Number]
Address: [Insert Address]

24. Governing Law
This Policy is governed by applicable laws in countries where Qureo operates, including the Republic of Liberia.

25. Acknowledgment
By using Qureo, you confirm that you have read, understood, and agreed to this Privacy Policy.


QUREO PRIVACY SUMMARY (APP VERSION)
Your Health. Your Data. Protected.
We take your privacy seriously. Here’s a simple overview of how your data is used on Qureo:

1. What we collect
We collect only what we need to support your care:
Basic info (name, phone, age, location)
Health information you share (symptoms, history, results)
Payments and wallet activity
App usage (to improve your experience)

2. How we use your data
Your data helps us:
Give you health guidance and AI support
Connect you to doctors, labs, and pharmacies
Manage your wallet and insurance
Improve services and user experience

3. AI and your health
Our AI helps guide you, not diagnose you
You can always speak to a real healthcare provider
We continuously improve accuracy and reduce bias

4. Who we share data with
Only when necessary to serve you:
Doctors, pharmacies, and labs
Payment providers and insurers
Trusted technology partners
We never sell your personal or health data.

5. Your data is secure
We protect your data using:
Encryption and secure systems
Access controls and monitoring
Continuous security improvements

6. You are in control
You can:
Access or update your data
Request deletion
Control app permissions
Choose what you share

7. Important to know
Qureo is not an emergency service
Always seek care for urgent medical conditions

Want more details?
You can view our full Privacy Policy anytime in the app.
By using Qureo, you agree to how we handle your data to provide safe, accessible care.


Business Associate / Data Processing Agreements
Data Processing Agreements (DPA)
Confidentiality clauses

Consent Architecture (Not Just Text)
Incident & Breach Response SOP
Content & Health Information Disclaimer Policy


Layer
Status
Terms & Conditions
Strong
Privacy Policy
Strong
In-app consent
Good
AI disclaimer
Good
Provider terms
Good
Clinical safety protocols
Missing
Data sharing with MoH
Missing
Data processing agreements
Missing
Telemedicine policy
Missing
Consent tracking system
Missing
Incident response SOP
Missing`,
};

const summaryCards = [
  {
    key: "care",
    iconSrc: StethoscopeIcon,
    tileBg: "color-mix(in srgb, var(--primary) 14%, white)",
    badgeBg: "var(--primary)",
    badgeText: "✓",
    text: "Qureo supports decisions with AI, and licensed clinicians deliver care.",
  },
  {
    key: "privacy",
    iconSrc: DefenceIcon,
    tileBg: "color-mix(in srgb, var(--primary) 12%, white)",
    badgeBg: "var(--primary)",
    badgeText: "✓",
    text: "Your health data is encrypted, protected, and used only for care experiences.",
  },
  {
    key: "wallet",
    iconSrc: WalletIcon,
    tileBg: "color-mix(in srgb, var(--primary) 10%, white)",
    badgeBg: "var(--primary)",
    badgeText: "✓",
    text: "Payments, wallet, and insurance features are handled via regulated partners.",
  },
  {
    key: "emergency",
    iconSrc: HospitalIcon,
    tileBg: "rgba(244, 63, 94, 0.12)",
    badgeBg: "#ef4444",
    badgeText: "!",
    text: "For emergencies, call local emergency services or go to the nearest hospital.",
  },
];

const pageStyle = {
  minHeight: "100vh",
  background: "radial-gradient(1200px 500px at -10% -10%, color-mix(in srgb, var(--primary) 12%, white) 0%, transparent 60%), radial-gradient(900px 400px at 110% 0%, color-mix(in srgb, var(--primary) 10%, white) 0%, transparent 58%), linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%)",
  padding: "0 14px 30px",
};

const shellStyle = {
  width: "100%",
  maxWidth: 440,
  margin: "0 auto",
};

const cardStyle = {
  background: "#ffffff",
  borderRadius: 16,
  border: "1px solid color-mix(in srgb, var(--primary) 16%, white)",
  boxShadow: "0 10px 26px rgba(15, 23, 42, 0.08)",
};

const docButtonStyle = (active) => ({
  flex: 1,
  borderRadius: 12,
  border: active ? "1px solid var(--primary)" : "1px solid rgba(30, 41, 59, 0.12)",
  background: active ? "color-mix(in srgb, var(--primary) 12%, white)" : "#fff",
  color: active ? "var(--primary)" : "#334155",
  fontWeight: 700,
  fontSize: 13.5,
  padding: "11px 10px",
  boxShadow: "0 2px 8px rgba(30, 41, 59, 0.06)",
});

const isDocumentTitle = (line) => {
  if (!line) return false;
  return /^[A-Z0-9 &/().,+:-]+$/.test(line) && line.length > 10;
};

const isSectionHeading = (line) => /^\d+(\.\d+)?\.\s+/.test(line);

const isSubheading = (line) => {
  if (!line) return false;
  if (isDocumentTitle(line) || isSectionHeading(line)) return false;
  return /^[A-Z][A-Za-z0-9 &/(),+-]+:?$/.test(line) && line.length < 70;
};

function parseLegalContent(text) {
  const lines = text.split("\n");
  const blocks = [];
  let listBuffer = [];
  let paragraphBuffer = [];
  let collectList = false;

  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: "list", items: [...listBuffer] });
      listBuffer = [];
    }
  };

  const flushParagraph = () => {
    if (paragraphBuffer.length) {
      blocks.push({ type: "paragraph", text: paragraphBuffer.join(" ") });
      paragraphBuffer = [];
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      collectList = false;
      return;
    }

    if (isDocumentTitle(line)) {
      flushParagraph();
      flushList();
      collectList = false;
      blocks.push({ type: "docTitle", text: line });
      return;
    }

    if (/^(Effective Date|Last Updated|Version|Applies to):/i.test(line)) {
      flushParagraph();
      flushList();
      collectList = false;
      blocks.push({ type: "meta", text: line });
      return;
    }

    if (isSectionHeading(line)) {
      flushParagraph();
      flushList();
      collectList = false;
      blocks.push({ type: "section", text: line });
      return;
    }

    if (line.endsWith(":")) {
      flushParagraph();
      flushList();
      collectList = true;
      blocks.push({ type: "subheading", text: line.replace(/:$/, "") });
      return;
    }

    if (collectList || isSubheading(line)) {
      if (isSubheading(line) && !collectList) {
        flushParagraph();
        flushList();
        blocks.push({ type: "subheading", text: line.replace(/:$/, "") });
        collectList = true;
        return;
      }

      if (line.length < 120) {
        listBuffer.push(line);
        return;
      }
    }

    flushList();
    collectList = false;
    paragraphBuffer.push(line);
  });

  flushParagraph();
  flushList();
  return blocks;
}

function LegalDocument({ content }) {
  const blocks = useMemo(() => parseLegalContent(content), [content]);

  return (
    <div>
      {blocks.map((block, index) => {
        if (block.type === "docTitle") {
          return (
            <div key={`${block.type}-${index}`} style={{ marginTop: index === 0 ? 0 : 18, marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#1e3a8a", letterSpacing: 0.2 }}>
                {block.text}
              </h3>
            </div>
          );
        }

        if (block.type === "meta") {
          return (
            <div key={`${block.type}-${index}`} style={{ fontSize: 12.5, color: "#64748b", marginBottom: 4 }}>
              {block.text}
            </div>
          );
        }

        if (block.type === "section") {
          return (
            <h4
              key={`${block.type}-${index}`}
              style={{
                margin: "18px 0 8px",
                fontSize: 14.5,
                fontWeight: 800,
                color: "#334155",
              }}
            >
              {block.text}
            </h4>
          );
        }

        if (block.type === "subheading") {
          return (
            <div
              key={`${block.type}-${index}`}
              style={{
                marginTop: 12,
                marginBottom: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#475569",
              }}
            >
              {block.text}
            </div>
          );
        }

        if (block.type === "list") {
          return (
            <ul key={`${block.type}-${index}`} style={{ margin: "0 0 10px 18px", padding: 0, color: "#475569" }}>
              {block.items.map((item) => (
                <li key={item} style={{ marginBottom: 5, lineHeight: 1.55, fontSize: 12.8 }}>
                  {item}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`${block.type}-${index}`} style={{ margin: "0 0 10px", color: "#475569", fontSize: 12.8, lineHeight: 1.62 }}>
            {block.text}
          </p>
        );
      })}
    </div>
  );
}

export default function TermsPolicyPage({ embedded = false, forceSignupMode = false, onAccept, onClose, disableClose = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSignup = forceSignupMode || new URLSearchParams(location.search).get("from") === "signup";
  const [activeDoc, setActiveDoc] = useState(null);

  const ctaLabel = useMemo(() => (fromSignup ? "Agree & Continue" : "Done"), [fromSignup]);

  const handlePrimaryAction = () => {
    if (typeof onAccept === "function") {
      onAccept();
      return;
    }

    if (fromSignup) {
      localStorage.setItem("signup_terms_accepted", "true");
      navigate("/signup?policyAccepted=1", { replace: true });
      return;
    }
    navigate(-1);
  };

  return (
    <div style={embedded ? { ...pageStyle, minHeight: "auto", padding: 0, background: "transparent" } : pageStyle}>
      <div style={shellStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: 10,
            marginBottom: 6,
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (disableClose) return;
              if (typeof onClose === "function") {
                onClose();
                return;
              }
              navigate(-1);
            }}
            style={{
              width: 84,
              height: 84,
              borderRadius: 999,
              border: "none",
              background: "transparent",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: disableClose ? "transparent" : "#64748b",
              cursor: disableClose ? "default" : "pointer",
            }}
            aria-hidden={disableClose}
            tabIndex={disableClose ? -1 : 0}
          >
            {!disableClose && <ChevronLeft size={34} />}
          </button>



          <div style={{ width: 84 }} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          style={{
            textAlign: "center",
            marginBottom: 14,
            padding: "18px 16px",
            borderRadius: 20,
            background: "linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, white) 100%)",
            color: "#ffffff",
            boxShadow: "0 14px 34px rgba(15,23,42,0.22)",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 23,
              lineHeight: 1.2,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Trust, Privacy, and Care
          </h1>
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 12.5,
              color: "rgba(255,255,255,0.92)",
              fontWeight: 600,
            }}
          >
            Please review how Qureo protects your data and supports safe healthcare use.
          </p>
        </motion.div>

        <div style={{ height: 1, background: "rgba(148,163,184,0.32)", marginBottom: 14 }} />

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.24 }}
          style={{
            fontSize: 13.5,
            color: "#334155",
            fontWeight: 700,
            marginBottom: 12,
          }}
        >
          Before you create your account, here is what you should know:
        </motion.p>

        <div style={{ display: "grid", gap: 10 }}>
          {summaryCards.map((item, index) => {
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + index * 0.05, duration: 0.24 }}
                style={{
                  ...cardStyle,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "13px 14px",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                      borderRadius: 12,
                    background: item.tileBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <img
                    src={item.iconSrc}
                    alt=""
                    style={{
                      width: 25,
                      height: 25,
                      objectFit: "contain",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: -4,
                      bottom: -3,
                      width: 16,
                      height: 16,
                      borderRadius: 999,
                      background: item.badgeBg,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
                    }}
                  >
                    {item.badgeText}
                  </span>
                </div>
                <p
                  style={{
                    margin: 0,
                      fontSize: 13.1,
                      lineHeight: 1.45,
                      color: "#334155",
                      fontWeight: 500,
                  }}
                >
                  {item.text}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22, duration: 0.24 }}
          style={{ textAlign: "center", marginTop: 18 }}
        >
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 800,
              color: "#1e293b",
              marginBottom: 7,
            }}
          >
            By tapping “Agree & Continue”,
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
            You agree to the:
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={() => setActiveDoc((current) => (current === "terms" ? null : "terms"))}
              style={docButtonStyle(activeDoc === "terms")}
            >
              Terms & Conditions
            </button>
            <button
              type="button"
              onClick={() => setActiveDoc((current) => (current === "privacy" ? null : "privacy"))}
              style={docButtonStyle(activeDoc === "privacy")}
            >
              Privacy Policy
            </button>
          </div>
        </motion.div>

        <AnimatePresence initial={false}>
          {activeDoc && (
            <motion.div
              key={activeDoc}
              initial={{ opacity: 0, height: 0, y: 8 }}
              animate={{ opacity: 1, height: "auto", y: 0 }}
              exit={{ opacity: 0, height: 0, y: -6 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              style={{ overflow: "hidden", marginTop: 14 }}
            >
              <div style={{ ...cardStyle, padding: 16 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#1e293b",
                    marginBottom: 10,
                  }}
                >
                  {activeDoc === "terms" ? "Terms & Conditions" : "Privacy Policy"}
                </div>
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  <LegalDocument content={legalContent[activeDoc]} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          type="button"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.25 }}
          onClick={handlePrimaryAction}
          style={{
            width: "100%",
            marginTop: 20,
            border: "none",
            borderRadius: 999,
            padding: "14px 20px",
            background: "var(--primary)",
            color: "#fff",
            fontSize: 15.5,
            fontWeight: 800,
            boxShadow: "0 12px 26px rgba(15,23,42,0.22)",
            cursor: "pointer",
          }}
        >
          {ctaLabel}
        </motion.button>
      </div>
    </div>
  );
}
